import * as React from 'react';

import {Stack} from 'shells/layout/stack/stack';

import {useGetPersonalizedDemo} from '../../navigation/trial-banner/graphql/hooks/use-get-personalized-demo';
import {useHasFullSchedulerAccess} from '../../scheduler/create-modals/multi-host/use-has-full-scheduler-access';
import {useGetCalendarSync} from '../../setup/calendar/graphql/hooks/use-get-calendar-sync';

import {EmailCalendarSyncModal as EmailCalendarSyncModalOld} from './email-calendar-sync-modal/email-calendar-sync-modal';
import type {EmailCalendarSyncSettings} from './email-calendar-sync-modal/email-calendar-sync-settings-page';
import {EmailCalendarSyncModal} from './email-calendar-sync-modal-new/email-calendar-sync-modal';
import {ExplorePageBase} from './explore-page-base';
import {useGetEmailCalendarStatuses} from './graphql/hooks/use-get-email-calendar-statuses';
import {useGetTrialOnboarding} from './graphql/hooks/use-get-trial-onboarding';
import {useGetUserVideoIntegrations} from './graphql/hooks/use-get-user-video-integrations';
import {useGoogleSetInitialSyncSettings} from './graphql/hooks/use-google-set-initial-sync-settings';
import {useOffice365SetInitialSyncSettings} from './graphql/hooks/use-office365-set-initial-sync-settings';
import {useOnboardingRefetchListeners} from './hooks/use-onboarding-refetch-listeners';
import {IntroWebcastBanner} from './intro-webcast-banner';
import {NutshellAiSection} from './nutshell-ai-section';
import {OnboardingModulesList} from './onboarding-module/onboarding-modules-list';
import {OnboardingScheduleCard} from './onboarding-schedule-card';
import {UpcomingOffersSection} from './upcoming-offers/upcoming-offers-section';

export function TrialExplorePage() {
    const {trialOnboarding, isLoading, refetch} = useGetTrialOnboarding();
    const {personalizedDemo, isLoading: isPersonalizedDemoLoading} = useGetPersonalizedDemo();
    const {hasFullSchedulerAccess} = useHasFullSchedulerAccess();
    const {
        userEmailCalendarStatuses,
        isLoading: isEmailCalendarStatusesLoading,
        refetch: refetchEmailCalendarStatuses,
    } = useGetEmailCalendarStatuses(!NutClientConfig.hasImprovedEmailCalendarSyncFF);
    const {userVideoIntegrations} = useGetUserVideoIntegrations();
    const {calendarSync, refetch: refetchCalendarSync} = useGetCalendarSync();
    const {googleSetInitialSyncSettings} = useGoogleSetInitialSyncSettings();
    const {office365SetInitialSyncSettings} = useOffice365SetInitialSyncSettings();

    const [isEmailCalendarSyncModalOpen, setIsEmailCalendarSyncModalOpen] = React.useState(false);
    const [connectedProvider, setConnectedProvider] = React.useState<'google' | 'microsoft' | null>(
        null
    );

    useOnboardingRefetchListeners(refetch, refetchEmailCalendarStatuses);

    const shouldShowLoadingState =
        (isLoading || isPersonalizedDemoLoading || isEmailCalendarStatusesLoading) &&
        !(trialOnboarding && !isPersonalizedDemoLoading);

    const handleEmailCalendarAuthSuccess = React.useCallback(
        (provider: 'google' | 'microsoft') => {
            setConnectedProvider(provider);
            refetchCalendarSync();
            setIsEmailCalendarSyncModalOpen(true);
            window.dispatchEvent(new Event('onboarding-step-completed-email-calendar-sync'));
        },
        [refetchCalendarSync]
    );

    const handleSyncSettingsSave = React.useCallback(
        (settings: EmailCalendarSyncSettings): Promise<any> => {
            if (!connectedProvider) {
                return Promise.resolve();
            }

            const mutation =
                connectedProvider === 'google'
                    ? googleSetInitialSyncSettings
                    : office365SetInitialSyncSettings;

            return mutation({variables: {input: settings}});
        },
        [connectedProvider, googleSetInitialSyncSettings, office365SetInitialSyncSettings]
    );

    return (
        <ExplorePageBase
            variant='trial'
            userEmailCalendarStatuses={userEmailCalendarStatuses}
            onEmailCalendarAuthSuccess={handleEmailCalendarAuthSuccess}
        >
            <Stack spacing={16}>
                <OnboardingModulesList
                    title='Tailored for you'
                    modules={trialOnboarding && trialOnboarding.tailoredModules}
                    isLoading={shouldShowLoadingState}
                    hasFullSchedulerAccess={hasFullSchedulerAccess}
                    userEmailCalendarStatuses={userEmailCalendarStatuses}
                    onboardingQuotes={trialOnboarding && trialOnboarding.onboardingQuotes}
                    userVideoIntegrations={userVideoIntegrations}
                    variant='trial'
                    onEmailCalendarAuthSuccess={handleEmailCalendarAuthSuccess}
                />
                {personalizedDemo && !shouldShowLoadingState && (
                    <OnboardingScheduleCard personalizedDemo={personalizedDemo} />
                )}
                {/* div wrapper prevents Card's height:100% from expanding as a flex child of Stack,
                   which would squish OnboardingScheduleCard to ~20px (BILL-254, BILL-220) */}
                {!shouldShowLoadingState && (
                    <div>
                        <IntroWebcastBanner canDismiss={false} />
                    </div>
                )}
            </Stack>
            <OnboardingModulesList
                title='Explore further'
                modules={trialOnboarding && trialOnboarding.additionalModules}
                isLoading={shouldShowLoadingState}
                hasFullSchedulerAccess={hasFullSchedulerAccess}
                userEmailCalendarStatuses={userEmailCalendarStatuses}
                onboardingQuotes={trialOnboarding && trialOnboarding.onboardingQuotes}
                userVideoIntegrations={userVideoIntegrations}
                variant='trial'
                onEmailCalendarAuthSuccess={handleEmailCalendarAuthSuccess}
            />
            <NutshellAiSection />
            <UpcomingOffersSection />
            {NutClientConfig.hasNewEmailSyncContactCreationFF ? (
                <EmailCalendarSyncModal
                    isOpen={isEmailCalendarSyncModalOpen}
                    onClose={() => setIsEmailCalendarSyncModalOpen(false)}
                    onSaveSyncSettings={handleSyncSettingsSave}
                    calendarSync={calendarSync}
                />
            ) : (
                <EmailCalendarSyncModalOld
                    isOpen={isEmailCalendarSyncModalOpen}
                    onClose={() => setIsEmailCalendarSyncModalOpen(false)}
                    onSaveSyncSettings={handleSyncSettingsSave}
                    calendarSync={calendarSync}
                />
            )}
        </ExplorePageBase>
    );
}
